import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api';
import { AxiosError } from 'axios';

export interface PinterestBoard {
  id: string;
  name: string;
  description: string | null;
  privacy: 'PUBLIC' | 'PROTECTED' | 'SECRET';
  pinCount: number;
  followerCount: number;
}

/**
 * Hook to fetch Pinterest boards for a channel
 */
export const usePinterestBoards = (channelId: number | null) => {
  const query = useQuery({
    queryKey: ['pinterest-boards', channelId],
    queryFn: async () => {
      if (!channelId) return [];
      const response = await apiClient.get(`/api/channels/${channelId}/pinterest/boards`);
      return response.data.data as PinterestBoard[];
    },
    enabled: !!channelId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  });

  // Extract meaningful error message from API response
  const errorMessage = query.error
    ? ((query.error as AxiosError<{ error?: string; details?: { action?: string } }>)?.response?.data?.error ||
       (query.error as Error).message ||
       'Failed to load boards')
    : null;

  const needsReconnect = query.error
    ? (query.error as AxiosError<{ details?: { action?: string } }>)?.response?.data?.details?.action === 'reconnect_required'
    : false;

  return {
    ...query,
    errorMessage,
    needsReconnect,
  } as typeof query & { errorMessage: string | null; needsReconnect: boolean };
};
